import React, { useMemo, useState } from "react";
import { Modal, Input, Button, Tooltip, Empty, Popconfirm, message } from "antd";
import {
  PushpinOutlined,
  PushpinFilled,
  DeleteOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { addChange, updateIssue } from "api/issue/issueApi";
import moment from "moment";

const T = {
  ink: "#23343E",
  text: "#3A4952",
  muted: "#8D8D8D",
  sub: "#AEAEAE",
  line: "#EFEFEF",
  line2: "#E2E2E2",
  bg: "#FAFAFA",
  wash: "#E9F1F5",
  navy: "#455D6B",
  orange: "#FE6919",
  added: "#4FC92A",
};

const randomId = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

/**
 * 3D pin annotation skeleton.
 *
 * Usage (in IssueViewer):
 *  - Render this component inside the 3D viewer area.
 *  - Pass `pinModeRef` and `pendingUniqueIdRef` — shared state mutated by the
 *    ReactApi.findInJsonUsingViewer callback when the user clicks a 3D element.
 *  - When a pending uniqueId appears, this component opens a note dialog and
 *    persists a new issueObject with objectType="3d-pin".
 */
export default function Issue3DPins({
  issue,
  canEdit,
  pinModeRef,
  pendingUniqueId,
  onPendingHandled,
  onIssueUpdated,
  currentUser,
}) {
  const [pinMode, setPinMode] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  const pins = useMemo(
    () => (issue?.issueObjects || []).filter((o) => o.objectType === "3d-pin"),
    [issue]
  );

  // Keep the mutable ref in sync with React state so the WebGL-side callback
  // (which captures the ref once) can read the latest value.
  React.useEffect(() => {
    if (pinModeRef) pinModeRef.current = pinMode;
  }, [pinMode, pinModeRef]);

  const closeDialog = () => {
    setNoteText("");
    onPendingHandled && onPendingHandled();
  };

  const handleSave = async () => {
    if (!pendingUniqueId) return;
    const text = noteText.trim();
    if (!text) {
      message.warning("Add a note for this pin");
      return;
    }
    setSaving(true);
    try {
      const newPin = {
        objectId: randomId(),
        objectType: "3d-pin",
        createdBy: currentUser?._id
          ? { userId: currentUser._id }
          : currentUser?.isGuest
          ? { guestId: currentUser.id }
          : undefined,
        data: {
          uniqueId: pendingUniqueId,
          note: text,
          authorName: currentUser?.name || "Unknown",
        },
        style: {},
        transform: { x: 0, y: 0, scaleX: 1, scaleY: 1, rotation: 0 },
        visible: true,
        locked: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const nextObjects = [...(issue.issueObjects || []), newPin];
      const res = await updateIssue(issue._id, { issueObjects: nextObjects });
      // Record in changeHistory for activity stream
      try {
        await addChange(issue._id, {
          changeType: "add",
          objectType: "3d-pin",
          objectId: newPin.objectId,
          description: `Added 3D pin on ${pendingUniqueId}: ${text}`,
          newState: { uniqueId: pendingUniqueId, note: text },
        });
      } catch {
        /* non-fatal */
      }
      message.success("Pin added");
      onIssueUpdated && onIssueUpdated(res.data || res);
      closeDialog();
    } catch (err) {
      message.error(err?.message || "Failed to save pin");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pin) => {
    try {
      const nextObjects = (issue.issueObjects || []).filter(
        (o) => o.objectId !== pin.objectId
      );
      const res = await updateIssue(issue._id, { issueObjects: nextObjects });
      message.success("Pin removed");
      onIssueUpdated && onIssueUpdated(res.data || res);
    } catch (err) {
      message.error(err?.message || "Failed to remove pin");
    }
  };

  return (
    <>
      {/* Floating toolbar button */}
      {/* pointerEvents:auto because the layer this sits in is
          transparent to the mouse - see the canvas's overlay wrapper.
          Only this floating column takes clicks; everywhere else the
          pointer belongs to the 3D view underneath. */}
      <div style={{
        position: "absolute", top: 12, left: 12, zIndex: 5,
        display: "flex", flexDirection: "column", gap: 8,
        pointerEvents: "auto",
      }}>
        <Tooltip title={pinMode ? "Exit pin mode" : "Add 3D pin (click an element)"}>
          <Button
            size="middle"
            type={pinMode ? "primary" : "default"}
            disabled={!canEdit}
            icon={pinMode ? <PushpinFilled /> : <PushpinOutlined />}
            onClick={() => setPinMode((v) => !v)}
            style={pinMode ? { background: T.orange, borderColor: T.orange } : undefined}
          >
            {pinMode ? "Pin mode ON" : "Pin"}
          </Button>
        </Tooltip>
        {pins.length > 0 && (
          <div style={{
            background: "rgba(255,255,255,0.95)",
            border: `1px solid ${T.line2}`, borderRadius: 6,
            padding: "6px 10px", fontSize: 11, color: T.text,
            maxWidth: 260, maxHeight: 240, overflowY: "auto",
          }}>
            <div style={{
              fontWeight: 700, fontSize: 10, letterSpacing: ".06em",
              color: T.muted, marginBottom: 6,
            }}>
              PINS ({pins.length})
            </div>
            {pins.map((p, i) => (
              <div key={p.objectId} style={{
                display: "flex", gap: 6, marginBottom: 4,
                padding: "4px 0", borderBottom: i < pins.length - 1 ? `1px dashed ${T.line}` : "none",
              }}>
                <span style={{
                  width: 18, height: 18, borderRadius: "50%",
                  background: T.orange, color: "#fff", flexShrink: 0,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 700,
                }}>{i + 1}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 11.5, color: T.ink, fontWeight: 500,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {p.data?.note || "—"}
                  </div>
                  <div style={{ fontSize: 10, color: T.muted, display: "flex", alignItems: "center", gap: 4 }}>
                    <EnvironmentOutlined /> {String(p.data?.uniqueId || "").slice(0, 14)}…
                    <span style={{ marginLeft: "auto" }}>
                      {p.createdAt ? moment(p.createdAt).fromNow() : ""}
                    </span>
                  </div>
                </div>
                {canEdit && (
                  <Popconfirm
                    title="Remove pin?"
                    onConfirm={() => handleDelete(p)}
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      size="small"
                      type="text"
                      icon={<DeleteOutlined style={{ color: T.muted }} />}
                    />
                  </Popconfirm>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Note dialog — opens when an element is picked in pin mode */}
      <Modal
        open={!!pendingUniqueId}
        onCancel={closeDialog}
        onOk={handleSave}
        okText="Save pin"
        okButtonProps={{ loading: saving }}
        title="New 3D pin"
        width={460}
      >
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 6 }}>Element</div>
        <div style={{
          padding: "6px 10px", background: T.bg, borderRadius: 4,
          fontFamily: "monospace", fontSize: 11, color: T.text, marginBottom: 12,
        }}>
          {pendingUniqueId || ""}
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 6 }}>Note</div>
        <Input.TextArea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="What's wrong with this element? What should change?"
          autoSize={{ minRows: 3, maxRows: 6 }}
          autoFocus
        />
      </Modal>
    </>
  );
}
